/** Copyright (c) 2026 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { Box, Input, Text, Wrap, WrapItem } from '@chakra-ui/react';
import { UserWordCloudComponent } from '@karya/core';
import { ParsedParams } from '@karya/generic-task-react';
import { ChangeEventHandler } from 'react';
import { ComponentRendererProps } from '../ComponentRenderer';

// Chip background colors
const CHIP_COLOR = '#D4EAE5';
const CHIP_SELECTED_COLOR = '#A7DFC3';

/** Splits text into selectable words. */
export function getWordCloudOptions(text: string | null | undefined): string[] {
  return (text ?? '')
    .split(' ')
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
}

type ChipProps = {
  word: string;
  isSelected: boolean;
  onClick?: () => void;
};

function WordCloudChip({ word, isSelected, onClick }: ChipProps) {
  // Clickable chips render as buttons
  const interactiveProps = onClick ? ({ as: 'button', type: 'button' } as const) : {};
  return (
    <WrapItem maxWidth="100%">
      <Box
        {...interactiveProps}
        onClick={onClick}
        px="8px"
        py="2px"
        borderRadius="full"
        fontSize="sm"
        color="black"
        bg={isSelected ? CHIP_SELECTED_COLOR : CHIP_COLOR}
        maxWidth="100%"
        textAlign="start"
        whiteSpace="normal"
        overflowWrap="anywhere"
        cursor={onClick ? 'pointer' : 'default'}
      >
        {word}
      </Box>
    </WrapItem>
  );
}

/** Preview of the word cloud component. */
export function UserWordCloudRenderer(props: ComponentRendererProps) {
  const typedParams = props.node.params as ParsedParams<UserWordCloudComponent>;
  const component = props.node.component as UserWordCloudComponent;

  const options = getWordCloudOptions(typedParams.text);
  const selected = Array.isArray(typedParams.selected) ? (typedParams.selected as unknown[]) : [];
  const other = typeof typedParams.other === 'string' ? typedParams.other : '';
  const hasOther = component.other != null;

  if (props.isDocked) {
    if (!props.isCurrent || !hasOther) {
      return null;
    }

    const handleOtherChange: ChangeEventHandler<HTMLInputElement> = (e) => {
      if (component.other) {
        props.writeOutput(component.other, e.target.value);
      }
    };

    return (
      <Input
        aria-label="Other"
        placeholder="Other"
        value={other}
        onChange={handleOtherChange}
        size="sm"
        bg="white"
        color="black"
        borderRadius="md"
        width="100%"
      />
    );
  }

  const handleToggleWord = (word: string) => {
    if (!props.isCurrent) {
      return;
    }
    const nextSelected = selected.includes(word) ? selected.filter((w) => w !== word) : [...selected, word];
    props.writeOutput(component.selected, nextSelected);
  };

  if (!props.isCurrent) {
    const chosenWords = options.filter((word) => selected.includes(word));
    const hasOtherText = other.trim().length > 0;
    return (
      <Wrap spacing="2px" justify="flex-end" maxWidth="100%">
        {chosenWords.map((word, index) => (
          <WordCloudChip key={`${word}-${index}`} word={word} isSelected />
        ))}
        {hasOtherText && <WordCloudChip word={other.trim()} isSelected />}
        {chosenWords.length === 0 && !hasOtherText && <WordCloudChip word="N/A" isSelected />}
      </Wrap>
    );
  }

  if (options.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500">
        No words to select. Add some text to the word cloud component.
      </Text>
    );
  }

  return (
    <Wrap spacing="2px" justify="flex-end" maxWidth="100%">
      {options.map((word, index) => (
        <WordCloudChip
          key={`${word}-${index}`}
          word={word}
          isSelected={selected.includes(word)}
          onClick={() => handleToggleWord(word)}
        />
      ))}
    </Wrap>
  );
}
